import {
    AlertTriangle,
    Archive,
    Briefcase,
    Calculator,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Copy,
    LoaderCircle,
    MoreVertical,
    Pencil,
    Plus,
    RefreshCw,
    Sparkles,
    Tag,
    Trash2,
    Users,
    Zap,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './IncentiveTemplateDetailScreen.module.css'

const TYPE_META = {
    SERVICE_COMMISSION: { label: 'Service %', color: '#1565c0', bg: '#e3f2fd' },
    RETAIL_COMMISSION: { label: 'Retail %', color: '#00695c', bg: '#e0f2f1' },
    CATEGORY_COMMISSION: { label: 'By Category', color: '#6a1b9a', bg: '#f3e5f5' },
    SLAB_COMMISSION: { label: 'Tiered Slabs', color: '#e65100', bg: '#fff3e0' },
    TARGET_BONUS: { label: 'Target Bonus', color: '#2e7d32', bg: '#e8f5e9' },
    ATTENDANCE_BONUS: { label: 'Attendance Bonus', color: '#283593', bg: '#e8eaf6' },
    CUSTOM_INCENTIVE: { label: 'Custom', color: '#546e7a', bg: '#eceff1' },
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`

const formatDate = (dateValue) => {
    if (!dateValue) return '—'
    const d = new Date(dateValue)
    if (Number.isNaN(d.getTime())) return dateValue
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const formatSlabRange = (slab) => {
    const from = formatCurrency(slab?.revenueFrom)
    const to = slab?.revenueTo !== null && slab?.revenueTo !== undefined
        ? formatCurrency(slab.revenueTo)
        : 'No limit'
    return `${from} – ${to}`
}

const formatINR = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const SlabSimulationResult = ({ result }) => {
    if (!result) return null

    return (
        <div className={styles.simResult}>
            <div className={styles.simSummary}>
                <p className={styles.simLabel}>Revenue simulated</p>
                <p className={styles.simRevenue}>{formatINR(result.simulateRevenue)}</p>

                <p className={styles.simLabel}>Total commission earned</p>
                <p className={styles.simTotal}>{formatINR(result.totalCommission)}</p>

                <p className={styles.simEffective}>Effective rate: {Number(result.effectiveRate || 0).toFixed(4)}%</p>

                {result.capApplied && (
                    <p className={styles.capWarning}>
                        Cap applied — commission was {formatINR(result.commissionBeforeCap)}, reduced to cap of {formatINR(result.cappedAt)}.
                    </p>
                )}

                {result.isMixedMode && (
                    <p className={styles.modeNote}>Mixed slab modes — each slab uses its own computation method.</p>
                )}
            </div>

            <div className={styles.simTable}>
                <p className={styles.simTableTitle}>Step-by-step breakdown</p>
                {result.slabMode === 'ON_INCREMENTAL' && (
                    <p className={styles.modeLegend}>Incremental mode — each bracket rate applies only to revenue within it.</p>
                )}
                {result.slabMode === 'ON_TOTAL' && (
                    <p className={styles.modeLegend}>Total mode — matched slab rate applies to the entire revenue.</p>
                )}

                {(result.slabBreakdown || []).map((slab, index) => (
                    <div
                        key={`sim-${index}`}
                        className={`${styles.simRow} ${slab.isApplicable ? styles.simRowApplicable : ''} ${slab.isMatchedSlab ? styles.simRowMatched : ''}`}
                    >
                        <div>
                            <p className={styles.simRange}>{formatSlabRange(slab)}</p>
                            <p className={styles.simRate}>
                                {slab.commissionPercent}% {slab.slabMode === 'ON_TOTAL' ? '(On Total)' : ''}
                            </p>
                        </div>

                        <div className={styles.simRight}>
                            {slab.isApplicable ? (
                                <>
                                    <p className={styles.simBracket}>
                                        {slab.slabMode === 'ON_TOTAL'
                                            ? 'Full revenue'
                                            : `${formatINR(slab.revenueInBracket)} in bracket`}
                                    </p>
                                    <p className={styles.simEarned}>+ {formatINR(slab.commissionEarned)}</p>
                                </>
                            ) : (
                                <p className={styles.simNA}>{slab.slabMode === 'ON_TOTAL' ? 'Not matched' : 'Below bracket'}</p>
                            )}
                        </div>
                    </div>
                ))}

                <div className={styles.simTotalRow}>
                    <span>Total Commission</span>
                    <strong>{formatINR(result.totalCommission)}</strong>
                </div>
            </div>
        </div>
    )
}

const IncentiveTemplateDetailScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { templateIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''
    const menuRef = useRef(null)

    const routeTemplateName = location.state?.templateName || ''

    const [detailData, setDetailData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [activeTab, setActiveTab] = useState('COMPONENTS')
    const [removedExpanded, setRemovedExpanded] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [remarksExpandedMap, setRemarksExpandedMap] = useState({})
    const [simRevenueMap, setSimRevenueMap] = useState({})
    const [simResultMap, setSimResultMap] = useState({})
    const [simExpandedMap, setSimExpandedMap] = useState({})
    const [isSimulatingComponentId, setIsSimulatingComponentId] = useState('')

    const fetchTemplateDetail = useCallback(
        async ({ silent = false } = {}) => {
            if (!authCtx?.token || !storeIdentifier || !templateIdentifier) {
                if (!silent) setIsLoading(false)
                return
            }

            if (!silent) setIsLoading(true)
            else setIsRefreshing(true)

            try {
                const response = await fetch(BASE_URL_API + 'getIncentiveTemplateDetail', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier,
                        templateIdentifier,
                    }),
                })

                if (!response.ok) {
                    toast.error('Failed to load commission plan detail.')
                    return
                }

                const respo = await response.json()

                if (respo?.status === 'success') {
                    setDetailData(respo.response)
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                if (respo?.errorCode === 'COMP_004') {
                    toast.error(respo?.message || 'Commission plan not found.')
                    navigate(-1)
                    return
                }

                toast.error(respo?.message || 'Failed to load commission plan.')
                navigate(-1)
            } catch (error) {
                console.error('getIncentiveTemplateDetail error:', error)
                toast.error('Network error. Please check your connection.')
            } finally {
                setIsLoading(false)
                setIsRefreshing(false)
            }
        },
        [authCtx, navigate, storeIdentifier, templateIdentifier]
    )

    useEffect(() => {
        fetchTemplateDetail()
    }, [fetchTemplateDetail])

    useEffect(() => {
        if (!storeIdentifier) return
        fetchTemplateDetail({ silent: true })
    }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const onFocus = () => {
            if (storeIdentifier) fetchTemplateDetail({ silent: true })
        }
        const onVisibility = () => {
            if (document.visibilityState === 'visible') onFocus()
        }
        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [fetchTemplateDetail, storeIdentifier])

    useEffect(() => {
        const onOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', onOutside)
        return () => document.removeEventListener('mousedown', onOutside)
    }, [])

    const template = detailData?.template || null
    const summary = detailData?.summary || null
    const activeComponents = useMemo(
        () => (Array.isArray(detailData?.components?.active) ? detailData.components.active : []),
        [detailData]
    )
    const inactiveComponents = useMemo(
        () => (Array.isArray(detailData?.components?.inactive) ? detailData.components.inactive : []),
        [detailData]
    )
    const assignedStaff = useMemo(
        () => (Array.isArray(detailData?.assignedStaff) ? detailData.assignedStaff : []),
        [detailData]
    )
    const previewStats = useMemo(() => {
        const active = detailData?.components?.active ?? []
        const totalSlabs = active.reduce((sum, component) => sum + (component.slabs?.length ?? 0), 0)
        const totalCatRates = active.reduce((sum, component) => sum + (component.categoryRates?.length ?? 0), 0)
        return {
            componentCount: active.length,
            slabCount: totalSlabs,
            categoryRateCount: totalCatRates,
            componentTypes: detailData?.summary?.componentTypes ?? [],
        }
    }, [detailData])

    const displayName = template?.templateName || routeTemplateName || 'Commission Plan'
    const canArchive = (detailData?.totalStaffAssigned ?? 0) === 0
    const isArchived = template?.isActive === 0

    const goToAddRule = (prefilledType = '') => {
        setMenuOpen(false)
        navigate(`/payroll/incentive-structure/${templateIdentifier}/add-component`, {
            state: {
                templateIdentifier,
                templateName: displayName,
                prefilledType,
            },
        })
    }

    const goToEditRule = (component) => {
        navigate(`/payroll/incentive-structure/${templateIdentifier}/edit-component/${component.componentIdentifier}`, {
            state: {
                templateIdentifier,
                templateName: displayName,
                component,
            },
        })
    }

    const handleRemoveRule = (component) => {
        toast.info(`Remove flow for "${component.componentName}" will be available with API-23.`)
    }

    const handleDuplicate = () => {
        setMenuOpen(false)
        navigate(`/payroll/incentive-structure/${templateIdentifier}/duplicate`, {
            state: {
                sourceTemplateIdentifier: templateIdentifier,
                sourceTemplateName: displayName,
                previewStats,
            },
        })
    }

    const toggleRemarks = (staffIdentifier) => {
        setRemarksExpandedMap((prev) => ({
            ...prev,
            [staffIdentifier]: !prev[staffIdentifier],
        }))
    }

    const toggleSimulationPanel = (componentIdentifier) => {
        setSimExpandedMap((prev) => ({
            ...prev,
            [componentIdentifier]: !prev[componentIdentifier],
        }))
    }

    const runSimulation = async (component) => {
        const revenueInput = simRevenueMap[component.componentIdentifier]
        const revenue = Number(revenueInput)
        if (revenueInput === '' || Number.isNaN(revenue) || revenue < 0) {
            toast.error('Enter a revenue amount of ₹0 or more.')
            return
        }

        if (!authCtx?.token || !storeIdentifier) return

        try {
            setIsSimulatingComponentId(component.componentIdentifier)

            const response = await fetch(BASE_URL_API + 'simulateIncentiveComponentSlab', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    componentIdentifier: component.componentIdentifier,
                    simulateRevenue: revenue,
                }),
            })

            if (!response.ok) {
                toast.error('Simulation failed. Please try again.')
                return
            }

            const respo = await response.json()
            if (respo?.status === 'success') {
                setSimResultMap((prev) => ({ ...prev, [component.componentIdentifier]: respo.response }))
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            toast.error(respo?.message || 'Simulation failed.')
        } catch (error) {
            console.error('simulateIncentiveComponentSlab error:', error)
            toast.error('Simulation failed. Please check your network.')
        } finally {
            setIsSimulatingComponentId('')
        }
    }

    const renderComponentCard = (component, isInactive = false) => {
        const hasSlabs = component.componentType === 'SLAB_COMMISSION' && Array.isArray(component.slabs) && component.slabs.length > 0
        const hasCategoryRates =
            component.componentType === 'CATEGORY_COMMISSION' &&
            Array.isArray(component.categoryRates) &&
            component.categoryRates.length > 0

        const typeMeta = TYPE_META[component.componentType] || {
            label: component.componentType,
            color: '#546e7a',
            bg: '#eceff1',
        }

        return (
            <article
                key={component.componentIdentifier}
                className={`${styles.componentCard} ${isInactive ? styles.componentCardInactive : ''}`}
            >
                <div className={styles.componentTopRow}>
                    <div className={styles.componentTitleWrap}>
                        <h4 className={styles.componentName}>{component.componentName}</h4>
                        <span
                            className={styles.typeChip}
                            style={{ color: typeMeta.color, background: typeMeta.bg }}
                        >
                            {typeMeta.label}
                        </span>
                    </div>

                    {!isInactive && (
                        <div className={styles.componentActions}>
                            <button
                                type="button"
                                className={styles.iconBtn}
                                title="Edit"
                                onClick={() => goToEditRule(component)}
                            >
                                <Pencil size={13} />
                            </button>
                            <button
                                type="button"
                                className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                                title="Remove"
                                onClick={() => handleRemoveRule(component)}
                            >
                                <Trash2 size={13} />
                            </button>
                        </div>
                    )}
                </div>

                <p className={styles.valueDisplay}>{component.valueDisplay || '—'}</p>

                {hasSlabs && (
                    <div className={styles.detailTable}>
                        {component.slabs.map((slab) => (
                            <div key={slab.slabIdentifier} className={styles.detailRow}>
                                <span className={styles.detailPrimary}>{formatSlabRange(slab)}</span>
                                <span className={styles.detailSecondary}>{slab.commissionPercent}%</span>
                                <span className={styles.detailTertiary}>
                                    {slab.slabMode === 'ON_INCREMENTAL' ? 'Incremental' : 'On Total'}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {hasCategoryRates && (
                    <div className={styles.detailTable}>
                        {component.categoryRates.map((rate) => (
                            <div key={rate.rateIdentifier} className={styles.detailRow}>
                                <span className={styles.detailPrimary}>{rate.serviceCategoryName}</span>
                                <span className={styles.detailSecondary}>{rate.commissionRate}%</span>
                                <span className={styles.detailTertiary}>{rate.serviceCategoryCode || 'Category'}</span>
                            </div>
                        ))}
                    </div>
                )}

                <div className={styles.metaRow}>
                    {Number(component.minServiceCountForEligibility) > 0 && (
                        <span className={styles.metaChip}>
                            Min {component.minServiceCountForEligibility} services
                        </span>
                    )}
                    <span className={styles.metaChip}>
                        {Number(component.cappedAt) > 0
                            ? `Cap ${formatCurrency(component.cappedAt)}`
                            : 'No cap'}
                    </span>
                    {isInactive && <span className={styles.metaChipMuted}>Removed</span>}
                </div>

                {!isInactive && hasSlabs && (
                    <div className={styles.simSection}>
                        <button
                            type="button"
                            className={styles.simToggleBtn}
                            onClick={() => toggleSimulationPanel(component.componentIdentifier)}
                        >
                            <Calculator size={13} /> What Would X Earn?
                            {simExpandedMap[component.componentIdentifier] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>

                        {simExpandedMap[component.componentIdentifier] && (
                            <div className={styles.simPanel}>
                                <div className={styles.simInputRow}>
                                    <label className={styles.simInputLabel}>Simulate revenue</label>
                                    <input
                                        className={styles.simInput}
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="e.g. 35000"
                                        value={simRevenueMap[component.componentIdentifier] || ''}
                                        onChange={(event) => {
                                            const value = event.target.value
                                            setSimRevenueMap((prev) => ({ ...prev, [component.componentIdentifier]: value }))
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className={styles.simRunBtn}
                                        disabled={
                                            isSimulatingComponentId === component.componentIdentifier ||
                                            !(simRevenueMap[component.componentIdentifier] || '').trim()
                                        }
                                        onClick={() => runSimulation(component)}
                                    >
                                        {isSimulatingComponentId === component.componentIdentifier
                                            ? <><LoaderCircle size={13} className={styles.spin} /> ...</>
                                            : 'Calculate →'}
                                    </button>
                                </div>

                                <SlabSimulationResult result={simResultMap[component.componentIdentifier]} />
                            </div>
                        )}
                    </div>
                )}
            </article>
        )
    }

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>

                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Commission Plan</span>
                    <h1 className={styles.headerTitle}>{displayName}</h1>
                </div>

                <div className={styles.headerActions}>
                    {isRefreshing && <RefreshCw size={15} className={`${styles.spin} ${styles.refreshIcon}`} />}
                    <div className={styles.menuWrap} ref={menuRef}>
                        <button
                            type="button"
                            className={styles.menuBtn}
                            aria-label="More actions"
                            onClick={() => setMenuOpen((prev) => !prev)}
                        >
                            <MoreVertical size={16} />
                        </button>

                        {menuOpen && (
                            <div className={styles.dropdownMenu}>
                                <button type="button" className={styles.dropdownItem} onClick={goToAddRule}>
                                    <Plus size={14} /> Add Commission Rule
                                </button>
                                <button type="button" className={styles.dropdownItem} onClick={handleDuplicate}>
                                    <Copy size={14} /> Duplicate
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.dropdownItem} ${styles.dropdownItemDanger} ${!canArchive ? styles.dropdownItemDisabled : ''}`}
                                    onClick={() => {
                                        setMenuOpen(false)
                                        if (!canArchive) {
                                            toast.info('Cannot archive while staff are assigned.')
                                            return
                                        }
                                        toast.info('Archive flow will be enabled in the next API phase.')
                                    }}
                                >
                                    <Archive size={14} /> Archive Plan
                                    {!canArchive && <span className={styles.dropdownHint}>Assigned staff</span>}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {isLoading && (
                <section className={styles.loaderCard}>
                    <LoaderCircle size={24} className={styles.spin} />
                    <p>Loading commission plan details…</p>
                </section>
            )}

            {!isLoading && !storeIdentifier && (
                <section className={styles.alertCard}>
                    <AlertTriangle size={15} />
                    <div>
                        <strong>Select an active store to continue.</strong>
                        <p>Commission plan data is store-specific.</p>
                    </div>
                </section>
            )}

            {!isLoading && storeIdentifier && detailData && (
                <>
                    <section className={styles.infoCard}>
                        <div className={styles.infoCardTop}>
                            <div className={styles.infoNameRow}>
                                <span className={styles.infoName}>{template?.templateName || displayName}</span>
                                <span className={styles.versionBadge}>v{template?.versionNumber ?? 1}</span>
                                {isArchived && (
                                    <span className={styles.archivedBadge}>
                                        <Archive size={11} /> Archived
                                    </span>
                                )}
                            </div>
                        </div>

                        {template?.templateDescription && (
                            <p className={styles.infoDesc}>{template.templateDescription}</p>
                        )}

                        <p className={styles.infoMeta}>
                            Updated on {formatDate(template?.updatedOn)}
                        </p>
                    </section>

                    <section className={styles.typeSummaryRow}>
                        {(Array.isArray(summary?.componentTypes) ? summary.componentTypes : []).length > 0 ? (
                            (summary.componentTypes || []).map((type) => {
                                const meta = TYPE_META[type] || { label: type, color: '#546e7a', bg: '#eceff1' }
                                return (
                                    <span
                                        key={type}
                                        className={styles.summaryChip}
                                        style={{ color: meta.color, background: meta.bg }}
                                    >
                                        <Tag size={11} /> {meta.label}
                                    </span>
                                )
                            })
                        ) : (
                            <span className={styles.summaryChipMuted}>No rules configured</span>
                        )}
                    </section>

                    <section className={styles.tabRow}>
                        <button
                            type="button"
                            className={`${styles.tab} ${activeTab === 'COMPONENTS' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('COMPONENTS')}
                        >
                            <Zap size={14} /> Components ({summary?.activeComponentCount ?? 0})
                        </button>
                        <button
                            type="button"
                            className={`${styles.tab} ${activeTab === 'ASSIGNED_STAFF' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('ASSIGNED_STAFF')}
                        >
                            <Users size={14} /> Assigned Staff ({detailData?.totalStaffAssigned ?? 0})
                        </button>
                    </section>

                    <section className={styles.tabContent}>
                        {activeTab === 'COMPONENTS' && (
                            <>
                                {activeComponents.length === 0 ? (
                                    <div className={styles.emptyState}>
                                        <div className={styles.emptyIllo}>
                                            <Zap size={30} className={styles.emptyIlloIcon} />
                                        </div>
                                        <h3>No commission rules added yet</h3>
                                        <p>
                                            Add a commission rule to define how staff earn variable pay.
                                        </p>
                                        <button
                                            type="button"
                                            className={styles.emptyCtaBtn}
                                            onClick={() => goToAddRule('SERVICE_COMMISSION')}
                                        >
                                            <Plus size={14} /> Add First Rule
                                        </button>
                                    </div>
                                ) : (
                                    <div className={styles.cardsList}>
                                        {activeComponents.map((component) => renderComponentCard(component, false))}
                                    </div>
                                )}

                                {inactiveComponents.length > 0 && (
                                    <div className={styles.removedWrap}>
                                        <button
                                            type="button"
                                            className={styles.removedToggle}
                                            onClick={() => setRemovedExpanded((prev) => !prev)}
                                        >
                                            <span>Removed ({summary?.inactiveComponentCount ?? inactiveComponents.length})</span>
                                            {removedExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                                        </button>
                                        {removedExpanded && (
                                            <div className={styles.cardsList}>
                                                {inactiveComponents.map((component) => renderComponentCard(component, true))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        {activeTab === 'ASSIGNED_STAFF' && (
                            <>
                                {assignedStaff.length === 0 ? (
                                    <div className={styles.emptyState}>
                                        <div className={styles.emptyIllo}>
                                            <Users size={30} className={styles.emptyIlloIcon} />
                                        </div>
                                        <h3>No staff assigned to this plan</h3>
                                        <p>Assign this commission plan from staff payroll settings.</p>
                                        <button
                                            type="button"
                                            className={styles.emptyCtaBtn}
                                            onClick={() => navigate('/payroll/assign-incentive-plan', {
                                                state: {
                                                    templateIdentifier,
                                                    templateName: displayName,
                                                },
                                            })}
                                        >
                                            <Plus size={14} /> Assign Staff
                                        </button>
                                    </div>
                                ) : (
                                    <div className={styles.cardsList}>
                                        {assignedStaff.map((staff) => {
                                            const isRemarksExpanded = !!remarksExpandedMap[staff.staffIdentifier]
                                            return (
                                                <article
                                                    key={staff.staffIdentifier}
                                                    className={styles.staffCard}
                                                    onClick={() => navigate(`/payroll/staff-compensation/${staff.staffIdentifier}`, {
                                                        state: {
                                                            staffIdentifier: staff.staffIdentifier,
                                                            staffName: staff.staffName,
                                                        },
                                                    })}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                            navigate(`/payroll/staff-compensation/${staff.staffIdentifier}`, {
                                                                state: {
                                                                    staffIdentifier: staff.staffIdentifier,
                                                                    staffName: staff.staffName,
                                                                },
                                                            })
                                                        }
                                                    }}
                                                >
                                                    <div className={styles.staffTop}>
                                                        <div>
                                                            <h4 className={styles.staffName}>{staff.staffName}</h4>
                                                            <p className={styles.staffDesignation}>{staff.designationLabel || 'Staff'}</p>
                                                        </div>
                                                        <span className={styles.staffSince}>Active since {formatDate(staff.effectiveFrom)}</span>
                                                    </div>

                                                    {staff.mobile && (
                                                        <p className={styles.staffMeta}>Mobile: {staff.mobile}</p>
                                                    )}

                                                    {staff.assignmentRemarks && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className={styles.remarksToggle}
                                                                onClick={(event) => {
                                                                    event.stopPropagation()
                                                                    toggleRemarks(staff.staffIdentifier)
                                                                }}
                                                            >
                                                                <Briefcase size={13} /> Remarks
                                                                {isRemarksExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                            </button>
                                                            {isRemarksExpanded && (
                                                                <p className={styles.remarksText}>{staff.assignmentRemarks}</p>
                                                            )}
                                                        </>
                                                    )}
                                                </article>
                                            )
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                </>
            )}

            {!isLoading && detailData && activeTab === 'COMPONENTS' && (
                <button type="button" className={styles.fab} onClick={goToAddRule}>
                    <Plus size={16} /> Add Rule
                </button>
            )}
        </div>
    )
}

export default IncentiveTemplateDetailScreen